import { z } from 'zod'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { creditUsage } from '@/lib/queries/credit-usage'
import { model } from '@/lib/queries/models'
import { organization } from '@/lib/queries/organizations'
import { product } from '@/lib/queries/products'
import { scene } from '@/lib/queries/scenes'
import { vtoLook } from '@/lib/queries/vto-looks'
import { ScrollPage } from '@/components/layout/scroll-page'
import { WorkspaceHome } from '@/features/workspaces'
import { IrisHome } from '@/features/workspaces/iris/iris-home'
import { isIrisEnabled } from '@/features/workspaces/iris/lib/iris-access'
import { irisThreadMessages } from '@/features/workspaces/iris/lib/use-iris-threads'

// `thread` lives in the URL so the sidebar (rendered globally) and the
// chat (rendered here) share state without an extra context. It also makes
// conversations bookmarkable.
const searchSchema = z.object({
  thread: z.string().optional().catch(undefined),
})

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId/'
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ thread: search.thread }),
  loader: async ({ deps, context, params }) => {
    // Accounts with Iris land on it; everyone else keeps the generations home.
    if (isIrisEnabled(context.entitlements.features)) {
      // Warm a thread's stored history so it seeds `useChat` instantly. Fires
      // on navigation and whenever a `preload='viewport'` sidebar link resolves
      // (the loader runs with `loaderDeps.thread`). Non-blocking.
      if (deps.thread) {
        void context.queryClient
          .query(irisThreadMessages.findOne(deps.thread, params.workspaceId))
          .catch(() => {})
      }
      return
    }

    const pageSize = 4
    await Promise.all([
      context.queryClient.infiniteQuery({
        ...product.findManyByWorkspace(params.workspaceId, undefined, {
          pageSize,
        }),
        staleTime: 'static',
      }),
      context.queryClient.infiniteQuery({
        ...model.findManyByWorkspace(params.workspaceId, undefined, {
          pageSize,
        }),
        staleTime: 'static',
      }),
      context.queryClient.infiniteQuery({
        ...scene.findManyByWorkspace(params.workspaceId, undefined, {
          pageSize,
        }),
        staleTime: 'static',
      }),
      context.queryClient.infiniteQuery({
        ...vtoLook.findManyByWorkspace(params.workspaceId, undefined, {
          pageSize,
        }),
        staleTime: 'static',
      }),
    ])
  },
  staticData: { layout: 'fluid' },
  component: WorkspaceIndex,
})

function WorkspaceIndex() {
  const { orgId } = Route.useParams()
  const { data: org } = useSuspenseQuery(organization.findOne(orgId))
  const { data: entitlements } = useSuspenseQuery(
    creditUsage.entitlements(org.billing_account_id)
  )
  return isIrisEnabled(entitlements.features) ? (
    <IrisHome />
  ) : (
    <ScrollPage>
      <WorkspaceHome />
    </ScrollPage>
  )
}
